import { useAuth } from '@hourly/contexts/AuthContext';
import { getObjectDiff } from '@hourly/utilities';
import { Button, ButtonSize, ButtonVariant } from '@hourly-hds/components';
import { useState } from 'react';
import StepLayout from '../_shared/StepLayout';
import AnimatedLoader from '../components/AnimatedLoader';
import { Step } from '../data/models/step.model';
import { StepError, StepErrorKey } from '../data/models/stepError.model';
import Errors from './components/Errors';
import PartnerDetailForm, {
  validateForm,
  partnerDetailFormInitialValues,
  type PartnerFormError,
  type PartnerForm,
} from './components/PartnerDetailForm';
import {
  TaxPayerIdentificationType,
  type FederalTaxClassification,
} from './data/models/partner.model';
import { usePartner, type PartnerUpdateArgs } from './data/usePartner';
import styles from './PartnerDetails.module.css';

const PartnerDetailsErrorMap: Record<Partial<StepErrorKey>, Record<string, { title: string; message: string }>> = {
  IdentificationNumber: {
    Invalid: {
      title: 'Unable to verify Identification Number',
      message: 'The legal name and EIN does not match the IRS records. Please check the information or contact your sales representative.',
    },
    Duplicated: {
      title: 'Unable to verify Identification Number',
      message: 'Please review your information or contact your sales representative.',
    },
  }
  
};

const mapDisplayableErrors = (
  stepErrors: StepError[] = []
): Array<{ title: string; message: string }> => {
  return stepErrors.reduce((acc: Array<{ title: string; message: string }>, curr: StepError) => {
    const displayableError = PartnerDetailsErrorMap[curr.key]?.[curr.type];
    if (displayableError) {
      acc.push({ title: displayableError.title, message: displayableError.message });
    }

    return acc;
  }, []);
};

interface PartnerDetailsProps {
  step: Step;
  areDocumentsSigned: boolean;
  onContinue: () => void;
  onPrevious: () => void;
}

const PartnerDetails = ({
  step,
  areDocumentsSigned,
  onContinue,
  onPrevious,
}: PartnerDetailsProps) => {
  const {
    sessionState: { partnerId },
  } = useAuth();

  const [form, setForm] = useState<PartnerForm>(partnerDetailFormInitialValues);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [errors, setErrors] = useState<PartnerFormError | null>(null);
  const [isValidatingTaxpayerIdentificationNumber, setIsValidatingTaxpayerIdentificationNumber] =
    useState(false);

  const { partner, isUpdatePartnerLoading, updatePartner } = usePartner(partnerId!, (partner) =>
    setForm({
      legalName: partner.legalName,
      dba: partner.dba,
      taxpayerIdentificationType: partner.taxpayerIdentificationType,
      taxpayerIdentificationNumber: partner.taxpayerIdentificationNumber,
      federalTaxClassification: partner.federalTaxClassification,
      firstName: partner.firstName,
      lastName: partner.lastName,
      email: partner.email,
      phoneNumber: partner.phoneNumber,
      address: partner.address,
      address2: partner.address2,
      city: partner.city,
      state: partner.state,
      zipCode: partner.zipCode,
    })
  );

  const taxpayerIdentificationNumberErrorMessage =
    step.getBlockingErrorMessageByKey('IdentificationNumber');

  const validationErrors: Partial<PartnerFormError> = {
    taxpayerIdentificationNumber: taxpayerIdentificationNumberErrorMessage ? [' '] : undefined,
  };

  const formErrors: Partial<PartnerFormError> = {
    ...validationErrors,
    ...errors,
  };

  const handleChangePartnerFields = (fieldsToUpdate: Partial<PartnerForm>) => {
    if (formSubmitted) {
      const currentErrors = validateForm({ ...form, ...fieldsToUpdate });
      setErrors(currentErrors);
    }

    setForm((currentFormState) => ({
      ...currentFormState,
      ...fieldsToUpdate,
    }));
  };

  const handleVerifyFormAndContinue = () => {
    setFormSubmitted(true);
    const formErrors = validateForm(form);
    if (formErrors) {
      setErrors(formErrors);
      return;
    }

    const currentFormValues = {
      id: partnerId!,
      legalName: form.legalName,
      dba: form.dba ?? '',
      taxpayerIdentificationType: form.taxpayerIdentificationType as TaxPayerIdentificationType,
      taxpayerIdentificationNumber: form.taxpayerIdentificationNumber,
      federalTaxClassification: form.federalTaxClassification as FederalTaxClassification,
      firstName: form.firstName,
      lastName: form.lastName,
      address: form.address,
      address2: form.address2 ?? '',
      city: form.city,
      state: form.state,
      zipCode: form.zipCode,
    };

    const diff = getObjectDiff(partner!, currentFormValues);
    const shouldUpdatePartner = Object.values(diff).length > 0;

    if (!shouldUpdatePartner && step.isStepCompleted) {
      onContinue();
      return;
    }

    const userIsChangingToEINType =
      diff?.taxpayerIdentificationType === TaxPayerIdentificationType.EmployerIdentificationNumber;

    const userIsUpdatingExistingEINNumber =
      diff?.taxpayerIdentificationNumber &&
      currentFormValues.taxpayerIdentificationType ===
        TaxPayerIdentificationType.EmployerIdentificationNumber;

    if (userIsChangingToEINType || userIsUpdatingExistingEINNumber) {
      setIsValidatingTaxpayerIdentificationNumber(true);
    }

    updatePartner({ id: currentFormValues.id, ...diff } as PartnerUpdateArgs, {
      onSuccess: () => {
        setErrors(null);
        setIsValidatingTaxpayerIdentificationNumber(false);
        onContinue();
      },

      onFail: () => {
        setIsValidatingTaxpayerIdentificationNumber(false);
      },
    });
  };

  if (isValidatingTaxpayerIdentificationNumber) {
    return <AnimatedLoader>Confirming Federal Taxpayer Identification Number...</AnimatedLoader>;
  }

  return (
    <StepLayout>
      <StepLayout.Header>
        <StepLayout.Title>Complete Partner Details</StepLayout.Title>
        <div className={styles.errorsContainer}>
          <Errors errors={mapDisplayableErrors(step.blockingErrors || [])} />
        </div>
      </StepLayout.Header>
      <StepLayout.Content>
        <PartnerDetailForm
          partner={form}
          onChange={handleChangePartnerFields}
          errors={formErrors}
          disabled={areDocumentsSigned}
        />
      </StepLayout.Content>
      <StepLayout.Actions>
        <Button
          fullWidth
          label="Previous"
          size={ButtonSize.Large}
          onClick={onPrevious}
          disabled={isUpdatePartnerLoading}
        />
        <Button
          fullWidth
          label="Continue"
          variant={ButtonVariant.Primary}
          size={ButtonSize.Large}
          onClick={handleVerifyFormAndContinue}
          disabled={isUpdatePartnerLoading}
        />
      </StepLayout.Actions>
    </StepLayout>
  );
};

export default PartnerDetails;
